import React from 'react';
import { CpuIcon, PackageIcon, ShieldIcon, ZapIcon, ArrowRightIcon } from 'lucide-react';
/**
 * Home page categories section — featured Gen 3 card + 3 supporting cards.
 */
export function Categories() {
  const gen3Category = {
    title: 'Gen 3',
    description: 'Explore the newest generation of Daedo electronic scoring gear. Unmatched precision, redesigned fit, and tournament-ready performance.',
    icon: <ZapIcon className="w-12 h-12" data-id="element-606" />,
    linkText: 'Explore Gen 3',
    href: '/shop/gen-3/'
  };
  const categories = [{
    title: 'Daedo PSS Systems',
    description: 'Complete electronic scoring systems for competition and training.',
    icon: <CpuIcon className="w-8 h-8" data-id="element-607" />,
    linkText: 'Shop PSS Systems',
    href: '/shop/daedo-pss-system/',
    watermark: <svg className="absolute -bottom-4 -right-4 w-48 h-48 text-gray-900 opacity-[0.03] group-hover:opacity-[0.05] transition-opacity duration-500" viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-608">
          <path d="M10 90 L30 70 L50 70 L70 50 L90 50" data-id="element-609" />
          <path d="M30 90 L50 70 L70 70 L90 50" data-id="element-610" />
          <circle cx="30" cy="70" r="3" fill="currentColor" data-id="element-611" />
          <circle cx="70" cy="50" r="3" fill="currentColor" data-id="element-612" />
          <rect x="40" y="65" width="20" height="10" data-id="element-613" />
          <path d="M50 40 L70 20 L90 20" data-id="element-614" />
          <circle cx="70" cy="20" r="3" fill="currentColor" data-id="element-615" />
          <path d="M20 50 L40 30 L60 30" data-id="element-616" />
          <rect x="35" y="25" width="10" height="10" data-id="element-617" />
        </svg>
  }, {
    title: 'Daedo Products',
    description: 'Official Daedo gear including gloves, e-foot gear, headgear, and more.',
    icon: <PackageIcon className="w-8 h-8" data-id="element-618" />,
    linkText: 'Shop Daedo',
    href: '/shop/daedo-products/',
    watermark: <div className="absolute -right-6 top-1/2 -translate-y-1/2 -rotate-90 text-gray-900 opacity-[0.03] group-hover:opacity-[0.05] transition-opacity duration-500 font-black text-7xl tracking-tighter select-none pointer-events-none origin-center" data-id="element-619">
          DAEDO
        </div>
  }, {
    title: 'Wacoku Products',
    description: 'WT-approved protective equipment, uniforms, and training accessories.',
    icon: <ShieldIcon className="w-8 h-8" data-id="element-620" />,
    linkText: 'Shop Wacoku',
    href: '/shop/wacoku-products/',
    watermark: <div className="absolute -right-8 top-1/2 -translate-y-1/2 -rotate-90 text-gray-900 opacity-[0.03] group-hover:opacity-[0.05] transition-opacity duration-500 font-black text-6xl tracking-tighter select-none pointer-events-none origin-center" data-id="element-621">
          WACOKU
        </div>
  }];
  return <section className="py-16 bg-white max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-622">
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 lg:p-12" data-id="element-623">
        <div className="text-center mb-10" data-id="element-624">
          <h2 className="text-3xl font-black text-gray-900 uppercase tracking-tight" data-id="element-625">
            Categories
          </h2>
          <div className="w-24 h-1 bg-blue-600 mx-auto mt-4" data-id="element-626"></div>
          <p className="mt-4 text-gray-600 font-medium max-w-2xl mx-auto" data-id="element-627">
            Browse our full selection of Daedo and Wacoku Taekwondo equipment.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6" data-id="element-628">
          <a href={gen3Category.href} className="group lg:w-5/12 flex flex-col justify-between p-8 sm:p-10 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 border border-blue-500 shadow-lg transition-all duration-300 relative overflow-hidden" data-id="element-629">
            <div className="absolute -right-10 -top-10 text-white opacity-10 group-hover:scale-110 group-hover:rotate-12 transition-transform duration-500" data-id="element-630">
              <ZapIcon className="w-64 h-64" data-id="element-631" />
            </div>

            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-632">
              <div className="absolute top-[-20%] left-[-10%] w-[150%] h-[150%] bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-white/10 via-transparent to-transparent opacity-50 transform -rotate-45" data-id="element-633"></div>
              <div className="absolute bottom-0 right-0 w-64 h-64 bg-white opacity-[0.03] transform rotate-45 translate-x-1/2 translate-y-1/4" data-id="element-634"></div>
              <div className="absolute top-1/4 -left-12 w-32 h-96 bg-white opacity-[0.02] transform rotate-12" data-id="element-635"></div>
            </div>

            <div className="relative z-10" data-id="element-636">
              <div className="w-20 h-20 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center text-white mb-8 border border-white/30 shadow-inner" data-id="element-637">
                {gen3Category.icon}
              </div>
              <h3 className="text-3xl font-black text-white mb-4 uppercase tracking-tight" data-id="element-638">
                {gen3Category.title}
              </h3>
              <p className="text-blue-100 text-lg mb-8 leading-relaxed" data-id="element-639">
                {gen3Category.description}
              </p>
            </div>

            <div className="relative z-10 flex items-center text-white font-bold text-lg uppercase tracking-wider group-hover:text-blue-200 mt-auto" data-id="element-640">
              {gen3Category.linkText}
              <div className="ml-3 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center group-hover:bg-white group-hover:text-blue-700 transition-colors" data-id="element-641">
                <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-642" />
              </div>
            </div>
          </a>

          <div className="lg:w-7/12 grid grid-cols-1 sm:grid-cols-3 gap-6" data-id="element-643">
            {categories.map(category => <a key={category.title} href={category.href} className="group relative overflow-hidden flex flex-col items-start p-6 rounded-xl bg-gray-50 hover:bg-blue-50 border border-transparent hover:border-blue-100 transition-all duration-300 h-full" data-id="element-644">
                {category.watermark}
                <div className="absolute inset-0 bg-gradient-to-r from-gray-50/80 via-transparent to-transparent group-hover:from-blue-50/80 transition-colors duration-300 pointer-events-none" data-id="element-645"></div>

                <div className="relative z-10 w-14 h-14 rounded-lg bg-white shadow-sm flex items-center justify-center text-blue-600 mb-5 group-hover:scale-110 group-hover:bg-blue-600 group-hover:text-white transition-all duration-300" data-id="element-646">
                  {category.icon}
                </div>
                <h3 className="relative z-10 text-xl font-bold text-gray-900 mb-2" data-id="element-647">
                  {category.title}
                </h3>
                <p className="relative z-10 text-gray-600 text-sm mb-6 flex-grow" data-id="element-648">
                  {category.description}
                </p>
                <div className="relative z-10 flex items-center text-blue-600 font-bold text-sm uppercase tracking-wider group-hover:text-blue-700 mt-auto" data-id="element-649">
                  {category.linkText}
                  <ArrowRightIcon className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-650" />
                </div>
              </a>)}
          </div>
        </div>
      </div>
    </section>;
}